<template>
  <div class="booked-commissions-report">
    <!-- Filters -->
    <div class="d-flex flex-wrap align-items-end gap-3 mb-3">
      <div class="referrer-filter">
        <label class="form-label small text-muted mb-1">Referrers</label>
        <MultiSelectComponent
          v-model="selectedReferrerIds"
          :options="referrerOptions"
          placeholder="Select referrers"
          size="sm"
        />
      </div>
      <div>
        <label class="form-label small text-muted mb-1 d-block"
          >Date range (by created date)</label
        >
        <DateRangePickerComponent
          v-model="dateRange"
          placeholder="All dates"
          @select="loadReport"
          @clear="loadReport"
        />
      </div>
      <div v-if="report.referrers.length > 0" class="ms-auto d-flex gap-2">
        <button class="btn btn-sm btn-outline-secondary" @click="expandAll">
          Expand all
        </button>
        <button class="btn btn-sm btn-outline-secondary" @click="collapseAll">
          Collapse all
        </button>
      </div>
    </div>

    <!-- Rollup -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-4 col-xl-2">
        <div class="border rounded p-3 h-100">
          <div
            class="small text-muted text-uppercase"
            title="What buyers pay for the sales behind these commissions (fee-based commissions: the fee)"
          >
            Gross revenue
          </div>
          <div class="fs-4 fw-bold">{{ formatEuro(report.gross_revenue) }}</div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl-2">
        <div class="border rounded p-3 h-100">
          <div
            class="small text-muted text-uppercase"
            title="Soldera's take: the commission fee, swap margin or fee the commissions are a cut of"
          >
            Net revenue
          </div>
          <div class="fs-4 fw-bold">{{ formatEuro(report.net_revenue) }}</div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl-2">
        <div class="border rounded p-3 h-100">
          <div class="small text-muted text-uppercase">Total earned</div>
          <div class="fs-4 fw-bold">{{ formatEuro(report.total_earned) }}</div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl-2">
        <div class="border rounded p-3 h-100">
          <div class="small text-muted text-uppercase">Paid</div>
          <div class="fs-4 fw-bold text-success">
            {{ formatEuro(report.paid) }}
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl-2">
        <div class="border rounded p-3 h-100">
          <div class="small text-muted text-uppercase">Requested</div>
          <div class="fs-4 fw-bold text-warning">
            {{ formatEuro(report.requested) }}
          </div>
        </div>
      </div>
      <div class="col-6 col-md-4 col-xl-2">
        <div class="border rounded p-3 h-100">
          <div class="small text-muted text-uppercase">Pending</div>
          <div class="fs-4 fw-bold text-secondary">
            {{ formatEuro(report.pending) }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-muted py-4 text-center">Loading…</div>
    <div
      v-else-if="report.referrers.length === 0"
      class="text-muted py-4 text-center"
    >
      No booked commissions for this selection.
    </div>

    <!-- Hierarchical table: referrer -> referred company -> sales transaction -->
    <div v-else class="table-responsive">
      <table class="table table-sm table-hover align-middle mb-0 booked-table">
        <thead class="table-light">
          <tr class="small text-muted">
            <th>Referrer / company / sale</th>
            <th>Date</th>
            <th class="text-end">MWh</th>
            <th class="text-end">Price</th>
            <th class="text-end">Soldera fee</th>
            <th>Referral rate</th>
            <th class="text-end">Commission</th>
            <th
              class="text-end"
              title="What the buyer pays for the sale (fee-based commissions: the fee)"
            >
              Gross revenue
            </th>
            <th
              class="text-end"
              title="Soldera's take: the commission fee, swap margin or fee the commission is a cut of"
            >
              Net revenue
            </th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <template
            v-for="rg in report.referrers"
            :key="rg.referrer_company_id"
          >
            <!-- Referrer row -->
            <tr
              class="expandable-row fw-bold border-top"
              @click="toggleReferrer(rg.referrer_company_id)"
            >
              <td>
                <FontAwesomeIcon
                  :icon="caret(isReferrerExpanded(rg.referrer_company_id))"
                  class="me-2 text-muted"
                  fixed-width
                />
                {{ rg.referrer_company_name ?? "Unknown referrer" }}
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td class="text-end">{{ formatEuro(rg.total) }}</td>
              <td class="text-end">{{ formatEuro(rg.gross_revenue) }}</td>
              <td class="text-end">{{ formatEuro(rg.net_revenue) }}</td>
              <td class="small fw-normal">
                <span
                  v-for="(part, idx) in statusParts(rg)"
                  :key="idx"
                  class="me-2"
                  :class="part.cls"
                  >{{ part.text }}</span
                >
              </td>
            </tr>

            <!-- Company rows -->
            <template v-if="isReferrerExpanded(rg.referrer_company_id)">
              <template
                v-for="cg in rg.companies"
                :key="
                  companyKey(rg.referrer_company_id, cg.referred_company_id)
                "
              >
                <tr
                  class="expandable-row"
                  @click="
                    toggleCompany(
                      rg.referrer_company_id,
                      cg.referred_company_id
                    )
                  "
                >
                  <td class="ps-4">
                    <FontAwesomeIcon
                      :icon="
                        caret(
                          isCompanyExpanded(
                            rg.referrer_company_id,
                            cg.referred_company_id
                          )
                        )
                      "
                      class="me-2 text-muted"
                      fixed-width
                    />
                    {{ cg.referred_company_name ?? "Unknown company" }}
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td class="text-end fw-medium">{{ formatEuro(cg.total) }}</td>
                  <td class="text-end">{{ formatEuro(cg.gross_revenue) }}</td>
                  <td class="text-end">{{ formatEuro(cg.net_revenue) }}</td>
                  <td class="small">
                    <span
                      v-for="(part, idx) in statusParts(cg)"
                      :key="idx"
                      class="me-2"
                      :class="part.cls"
                      >{{ part.text }}</span
                    >
                  </td>
                </tr>

                <!-- Sales transaction (deal) rows -->
                <template
                  v-if="
                    isCompanyExpanded(
                      rg.referrer_company_id,
                      cg.referred_company_id
                    )
                  "
                >
                  <tr
                    v-for="deal in cg.deals"
                    :key="deal.invoice_item_id"
                    class="small"
                  >
                    <td class="ps-5">
                      <router-link
                        v-if="deal.sales_transaction_id"
                        :to="{
                          name: 'AdminTransactionsEditView',
                          params: { id: deal.sales_transaction_id },
                        }"
                        @click.stop
                      >
                        Sale #{{ deal.sales_transaction_id }}
                      </router-link>
                      <span v-else-if="deal.source_fee_type">{{
                        feeTypeLabel(deal.source_fee_type)
                      }}</span>
                      <span v-else class="text-muted">—</span>
                      <span
                        v-if="deal.is_swap"
                        class="badge bg-info-subtle text-info-emphasis ms-1"
                        title="Swap deal: commission is a cut of the sale margin (sell − buy), not of Soldera's commission fee"
                        >swap</span
                      >
                      <EurConversionBadge :conversion="deal" class="ms-1" />
                    </td>
                    <td>
                      {{
                        dealDate(deal)
                          ? formatDateTimeIntoDate(dealDate(deal)!)
                          : "—"
                      }}
                    </td>
                    <td class="text-end">{{ formatMwh(deal.volume_mwh) }}</td>
                    <td class="text-end">
                      {{
                        deal.effective_price != null
                          ? formatEuro(deal.effective_price)
                          : "—"
                      }}
                    </td>
                    <td
                      class="text-end"
                      :title="deal.is_swap ? 'Sale margin (sell − buy)' : ''"
                    >
                      {{
                        deal.soldera_fee_total != null
                          ? formatEuro(deal.soldera_fee_total)
                          : "—"
                      }}
                      <span
                        v-if="deal.company_fee_rate != null"
                        class="text-muted"
                      >
                        ({{ formatPct(deal.company_fee_rate) }})
                      </span>
                    </td>
                    <td>
                      <span v-if="deal.referral_rate != null">{{
                        formatPct(deal.referral_rate)
                      }}</span>
                      <span v-else class="text-muted">—</span>
                      <span class="text-muted"> · {{ deal.tier_label }}</span>
                    </td>
                    <td class="text-end fw-medium">
                      {{ formatEuro(deal.commission_total) }}
                    </td>
                    <td class="text-end">
                      {{
                        deal.gross_revenue != null
                          ? formatEuro(deal.gross_revenue)
                          : "—"
                      }}
                    </td>
                    <td class="text-end">
                      {{
                        deal.net_revenue != null
                          ? formatEuro(deal.net_revenue)
                          : "—"
                      }}
                    </td>
                    <td>
                      <span class="badge" :class="statusClass(deal.status)">{{
                        deal.status
                      }}</span>
                    </td>
                  </tr>
                </template>
              </template>
            </template>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import DateRangePickerComponent from "@/components/common/DateRangePickerComponent.vue";
import type { DateRange } from "@/components/common/dateRange";
import { getBookedCommissions } from "@/admin/commission/commission";
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import EurConversionBadge from "@/money/EurConversionBadge.vue";
import {
  formatDateTimeIntoDate,
  formatEuro,
  formatMwh,
} from "@/composables/formatUtils";
import type {
  BookedCommissionDeal,
  BookedCommissions,
  BookedCommissionStatus,
} from "@/types";
import { ref, watch } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const props = defineProps<{
  referrerOptions: { label: string; value: number }[];
}>();

const { globalErrorHandler } = useGlobalHandlers();

const selectedReferrerIds = ref<number[]>([]);
const dateRange = ref<DateRange | null>(null);
const loading = ref(false);
const report = ref<BookedCommissions>(emptyReport());

const expandedReferrers = ref<Set<number>>(new Set());
const expandedCompanies = ref<Set<string>>(new Set());

function emptyReport(): BookedCommissions {
  return {
    referrers: [],
    total_earned: 0,
    paid: 0,
    requested: 0,
    pending: 0,
    gross_revenue: 0,
    net_revenue: 0,
  };
}

const companyKey = (referrerId: number, companyId: number) =>
  `${referrerId}:${companyId}`;

const isReferrerExpanded = (referrerId: number) =>
  expandedReferrers.value.has(referrerId);
const isCompanyExpanded = (referrerId: number, companyId: number) =>
  expandedCompanies.value.has(companyKey(referrerId, companyId));

const toggleReferrer = (referrerId: number) => {
  const next = new Set(expandedReferrers.value);
  if (next.has(referrerId)) {
    next.delete(referrerId);
  } else {
    next.add(referrerId);
  }
  expandedReferrers.value = next;
};

const toggleCompany = (referrerId: number, companyId: number) => {
  const key = companyKey(referrerId, companyId);
  const next = new Set(expandedCompanies.value);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  expandedCompanies.value = next;
};

const expandAll = () => {
  expandedReferrers.value = new Set(
    report.value.referrers.map((rg) => rg.referrer_company_id)
  );
  expandedCompanies.value = new Set(
    report.value.referrers.flatMap((rg) =>
      rg.companies.map((cg) =>
        companyKey(rg.referrer_company_id, cg.referred_company_id)
      )
    )
  );
};

const collapseAll = () => {
  expandedReferrers.value = new Set();
  expandedCompanies.value = new Set();
};

const caret = (expanded: boolean) =>
  expanded ? "fas fa-chevron-down" : "fas fa-chevron-right";

// Fee-based bonuses (no sale reference) carry the FEE_SOLDERA_* type they were derived from.
const FEE_TYPE_LABELS: Record<string, string> = {
  FEE_SOLDERA_ISSUANCE: "Issuance fee",
  FEE_SOLDERA_CANCELLATION: "Cancellation fee",
  FEE_SOLDERA_SERVICE: "Service fee",
  FEE_SOLDERA_PROCUREMENT: "Procurement fee",
  FEE_SOLDERA_TRANSFER: "Transfer fee",
};
const feeTypeLabel = (feeType: string) => FEE_TYPE_LABELS[feeType] ?? feeType;

// Fee-based bonuses have no sale date; show the booking (settlement) date instead.
const dealDate = (deal: BookedCommissionDeal) =>
  deal.sale_date ?? deal.created_at;
const formatPct = (value: number) => `${(value * 100).toFixed(1)}%`;
const statusClass = (status: BookedCommissionStatus) =>
  status === "paid"
    ? "bg-success"
    : status === "requested"
      ? "bg-warning text-dark"
      : "bg-secondary";

// Compact "paid / requested / pending" breakdown shown on the group rows (non-zero buckets only).
const statusParts = (group: {
  paid: number;
  requested: number;
  pending: number;
}) => {
  const parts: { text: string; cls: string }[] = [];
  if (group.paid) {
    parts.push({ text: `${formatEuro(group.paid)} paid`, cls: "text-success" });
  }
  if (group.requested) {
    parts.push({
      text: `${formatEuro(group.requested)} req`,
      cls: "text-warning",
    });
  }
  if (group.pending) {
    parts.push({
      text: `${formatEuro(group.pending)} pend`,
      cls: "text-secondary",
    });
  }
  return parts;
};

const loadReport = async () => {
  if (selectedReferrerIds.value.length === 0) {
    report.value = emptyReport();
    return;
  }
  loading.value = true;
  try {
    report.value = await getBookedCommissions(
      selectedReferrerIds.value,
      dateRange.value?.[0] ?? null,
      dateRange.value?.[1] ?? null
    );
    collapseAll();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    loading.value = false;
  }
};

watch(selectedReferrerIds, () => loadReport());

// Default to all referrers selected once the options arrive from the parent.
watch(
  () => props.referrerOptions,
  (options) => {
    if (options.length > 0 && selectedReferrerIds.value.length === 0) {
      selectedReferrerIds.value = options.map((option) => option.value);
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.referrer-filter {
  min-width: 280px;
}

.expandable-row {
  cursor: pointer;
}
</style>
